// Session panel: the workbench's "home" view.
//
// Slice-3 evolution of slice-2's proof-of-life. Same flow (health ping
// + create-session + run a smoke op) but now consumes SessionContext
// so the session it sets is visible to the rest of the workbench
// (TableExplorer, etc.).
//
// Once a session exists the user navigates to /workbench/tables to do
// real work — this panel is the entry/diagnostic surface.

import { useEffect, useState } from "react";
import { ApiError } from "../api/client";
import type { CreateDataSourceResponse } from "../api/client";
import type {
  HealthResponse,
  InstallationDescriptor,
  ListTablesResult,
  ProvenanceEntry,
} from "../api/types";
import { ConnectDataSourceModal } from "./ConnectDataSourceModal";
import { useSession } from "./SessionContext";

// Engine types the workbench knows how to render. We always show all
// three (DuckDB / SQLite / Polars) so the user sees the *concept* of
// engine selection even when the demo only has one engine registered.
// Engines with no installation registered are grouped under a disabled
// optgroup with a "not yet configured" hint.
const KNOWN_ENGINES: Array<{ id: "duckdb" | "sqlite" | "polars"; label: string }> = [
  { id: "duckdb", label: "DuckDB" },
  { id: "sqlite", label: "SQLite" },
  { id: "polars", label: "Polars" },
];

export function SessionPanel() {
  const { client, session, setSession, refreshSession } = useSession();
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);

  // The list of available installations, fetched from /installations.
  // Drives the data-source dropdown (grouped by engine type).
  const [installations, setInstallations] = useState<InstallationDescriptor[]>(
    [],
  );
  // The currently picked installation_id — null until the user selects
  // one, then "<engine>:<installation_id>" form is unnecessary because
  // installation_ids are unique within the registry.
  const [installationId, setInstallationId] = useState<string>("");
  // The user-set name of the AC being authored. Becomes the session's
  // primary identifier in the sidebar / chip / page headings (replaces
  // the opaque session_id slice). One session = one AC.
  const [acName, setAcName] = useState<string>("");

  const [tables, setTables] = useState<string[] | null>(null);
  const [log, setLog] = useState<ProvenanceEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Connection-manager wizard (slice C4) open state.
  const [showConnect, setShowConnect] = useState(false);
  const [connectMessage, setConnectMessage] = useState<string | null>(null);

  // Scope-tables picker state — which tables the user has selected as
  // "of interest". Seeded from session.scope_tables on session change.
  const [scopeSelected, setScopeSelected] = useState<Set<string>>(new Set());
  const [scopeSaving, setScopeSaving] = useState(false);
  const [scopeMessage, setScopeMessage] = useState<string | null>(null);

  useEffect(() => {
    client
      .health()
      .then(setHealth)
      .catch((e: unknown) => setHealthError(formatError(e)));
    // Fetch the list of available datasources on mount. Auto-select
    // the first one so the Create button is usable without a manual
    // pick when only one is available.
    client
      .listInstallations()
      .then((r) => {
        setInstallations(r.installations);
        if (r.installations.length > 0) {
          setInstallationId(r.installations[0]!.installation_id);
        }
      })
      .catch(() => {
        // Endpoint failing shouldn't crash the page — the user can
        // still type-in an installation_id via the fallback affordance.
        setInstallations([]);
      });
  }, [client]);

  useEffect(() => {
    // Whenever the active session changes, sync the log view.
    if (session === null) {
      setLog([]);
      return;
    }
    client
      .getSessionLog(session.session_id)
      .then((r) => setLog(r.log))
      .catch((e: unknown) => setError(formatError(e)));
  }, [client, session]);

  // On session change: auto-fetch the table list (cheap — no profiling)
  // and seed the scope-selection from the session's persisted scope_tables.
  useEffect(() => {
    if (session === null) {
      setTables(null);
      setScopeSelected(new Set());
      setScopeMessage(null);
      return;
    }
    setScopeSelected(new Set(session.scope_tables));
    setScopeMessage(null);
    client
      .runOp(session.session_id, "ListTables", { inputs: {} })
      .then((r) => {
        if (!r.success || !r.result) {
          throw new Error(r.error ?? "ListTables failed");
        }
        const result = r.result as unknown as ListTablesResult;
        setTables([...result.tables].sort());
      })
      .catch((e: unknown) => setError(formatError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, session?.session_id, session?.scope_tables.join("|")]);

  function toggleScope(t: string) {
    setScopeSelected((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
    setScopeMessage(null);
  }

  function selectAllScope() {
    if (tables) setScopeSelected(new Set(tables));
    setScopeMessage(null);
  }

  function clearAllScope() {
    setScopeSelected(new Set());
    setScopeMessage(null);
  }

  async function saveScope() {
    if (session === null) return;
    setScopeSaving(true);
    setError(null);
    setScopeMessage(null);
    try {
      const r = await client.runOp(session.session_id, "SetScopeTables", {
        inputs: { tables: Array.from(scopeSelected).sort() },
      });
      if (!r.success) {
        throw new Error(r.error ?? "SetScopeTables failed");
      }
      setScopeMessage(`Saved — ${scopeSelected.size} tables in scope.`);
      refreshSession();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setScopeSaving(false);
    }
  }

  // Called when the connect wizard materialises a new data source. Refresh
  // the installation list and auto-select the freshly-created source (its
  // installation_id == the source name), so "Start Lattice" is immediately
  // usable against it.
  function handleDataSourceCreated(resp: CreateDataSourceResponse) {
    setShowConnect(false);
    setConnectMessage(
      `Connected data source “${resp.name}” (${resp.backend_type}).` +
        (resp.registered ? "" : " Restart needed before it's queryable."),
    );
    client
      .listInstallations()
      .then((r) => {
        setInstallations(r.installations);
        const match = r.installations.find(
          (i) => i.installation_id === resp.name,
        );
        if (match) setInstallationId(match.installation_id);
      })
      .catch(() => {
        /* non-fatal: the source is written; list refresh can be retried */
      });
  }

  async function handleCreateSession() {
    if (installationId === "") {
      setError("Pick a data source first.");
      return;
    }
    if (acName.trim() === "") {
      setError("Give your Lattice a name first.");
      return;
    }
    setBusy(true);
    setError(null);
    setTables(null);
    try {
      // workspace_path is required by the wire protocol but the server
      // rewrites it to <volume>/<session_id>/ when running in hosted
      // mode, so any string works. We send a sentinel that's clearly
      // identifiable in logs if it ever leaks through.
      const info = await client.createSession({
        workspace_path: "session.coframe",
        installation_id: installationId,
        ac_name: acName.trim(),
      });
      setSession(info);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleSmokeOp() {
    if (session === null) return;
    setBusy(true);
    setError(null);
    try {
      const response = await client.runOp(session.session_id, "ListTables", {
        inputs: {},
      });
      if (!response.success || response.result === undefined || response.result === null) {
        throw new Error(response.error ?? "ListTables failed");
      }
      const result = response.result as unknown as ListTablesResult;
      setTables(result.tables);
      const refreshed = await client.getSessionLog(session.session_id);
      setLog(refreshed.log);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  }

  function handleClearSession() {
    setSession(null);
    setTables(null);
    setLog([]);
    setError(null);
  }

  // Group registered installations by engine type for the dropdown.
  // Engines that have NO installation registered get a disabled
  // placeholder under their optgroup, so the user can see we know
  // about the engine but haven't wired up a datasource for it.
  const byEngine = new Map<string, InstallationDescriptor[]>();
  for (const eng of KNOWN_ENGINES) byEngine.set(eng.id, []);
  for (const inst of installations) {
    if (inst.engine_type !== null) {
      const list = byEngine.get(inst.engine_type);
      if (list !== undefined) list.push(inst);
    }
  }

  return (
    <section>
      <h1>Lattice</h1>
      <p style={{ color: "#666", fontSize: "0.9rem", marginTop: "-0.25rem" }}>
        Start here: pick a <strong>data source</strong> (an engine + database)
        and name the Lattice you're about to build over it.
      </p>
      <HealthBlock health={health} error={healthError} />

      {session === null ? (
        <>
          <h2>Create</h2>
          <div style={formRowStyle}>
            <label style={labelStyle}>
              Lattice name
              <input
                value={acName}
                onChange={(e) => setAcName(e.target.value)}
                placeholder="e.g., retail-customer-360"
                style={inputStyle}
                autoFocus
              />
            </label>
            <label style={labelStyle}>
              data source (engine · installation)
              <select
                value={installationId}
                onChange={(e) => setInstallationId(e.target.value)}
                style={inputStyle}
              >
                {installationId === "" && (
                  <option value="">— pick one —</option>
                )}
                {KNOWN_ENGINES.map((eng) => {
                  const list = byEngine.get(eng.id) ?? [];
                  if (list.length === 0) {
                    return (
                      <optgroup key={eng.id} label={`${eng.label} (not yet configured)`}>
                        <option disabled value="">
                          — none registered —
                        </option>
                      </optgroup>
                    );
                  }
                  return (
                    <optgroup key={eng.id} label={eng.label}>
                      {list.map((inst) => (
                        <option
                          key={inst.installation_id}
                          value={inst.installation_id}
                        >
                          {inst.installation_id}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
            </label>
            <button
              onClick={handleCreateSession}
              disabled={busy || installationId === "" || acName.trim() === ""}
              style={buttonStyle}
            >
              Start Lattice
            </button>
            <button
              onClick={() => {
                setConnectMessage(null);
                setShowConnect(true);
              }}
              title="Connect a new engine + database and pin it as a data source."
              style={{ ...buttonStyle, background: "#1a7f4b" }}
            >
              + Connect data source…
            </button>
          </div>
          <p style={{ fontSize: "0.8rem", color: "#888", margin: "0.3rem 0 0 0" }}>
            One data source per Lattice. Pick an existing one above, or
            connect a new engine + database.
          </p>
          {connectMessage && (
            <div style={{ ...healthBlockStyle, marginTop: "0.6rem" }}>
              {connectMessage}
            </div>
          )}
        </>
      ) : (
        <>
          <div style={sessionBlockStyle}>
            <table>
              <tbody>
                <tr>
                  <td style={cellLabelStyle}>Lattice name</td>
                  <td><strong>{session.ac_name}</strong></td>
                </tr>
                <tr>
                  <td style={cellLabelStyle}>data source</td>
                  <td>{session.installation_id}</td>
                </tr>
                <tr>
                  <td style={cellLabelStyle}>session_id</td>
                  <td><code style={{ fontSize: "0.75rem", color: "#888" }}>{session.session_id}</code></td>
                </tr>
                <tr>
                  <td style={cellLabelStyle}>workspace_path</td>
                  <td><code>{session.workspace_path}</code></td>
                </tr>
                <tr>
                  <td style={cellLabelStyle}>scope_phase</td>
                  <td>{session.scope_phase}</td>
                </tr>
                <tr>
                  <td style={cellLabelStyle}>ops run</td>
                  <td>{session.operation_log_length}</td>
                </tr>
              </tbody>
            </table>
            <div style={{ marginTop: 12 }}>
              <button onClick={handleSmokeOp} disabled={busy} style={buttonStyle}>
                Smoke: ListTables
              </button>
              <button
                onClick={handleClearSession}
                disabled={busy}
                style={{ ...buttonStyle, marginLeft: 8, background: "#999" }}
              >
                Clear (close)
              </button>
            </div>
          </div>

          {/* Tables of interest — workbench redesign 2026-05-26. The user
              picks which tables this AC should cover BEFORE landing on
              AC Columns, so column-profile work is scoped to those tables
              and the page loads fast. */}
          {tables !== null && (
            <div style={scopeBlockStyle}>
              <div style={scopeHeaderRowStyle}>
                <div>
                  <h3 style={{ margin: 0 }}>Tables of interest</h3>
                  <p style={scopeHelpStyle}>
                    Pick the tables this Lattice should cover. Columns +
                    Lineage will profile only these, so the load stays
                    cheap on warehouses with many tables.
                  </p>
                </div>
                <div style={scopeStatsStyle}>
                  <strong>{scopeSelected.size}</strong>
                  <span style={{ color: "#888" }}> / {tables.length} selected</span>
                </div>
              </div>

              <div style={scopeChipRowStyle}>
                {tables.map((t) => {
                  const on = scopeSelected.has(t);
                  return (
                    <button
                      key={t}
                      onClick={() => toggleScope(t)}
                      style={on ? scopeChipOnStyle : scopeChipOffStyle}
                      title={on ? `Remove ${t} from scope` : `Add ${t} to scope`}
                    >
                      <span style={{ marginRight: 4 }}>{on ? "☑" : "☐"}</span>
                      {t}
                    </button>
                  );
                })}
              </div>

              <div style={scopeActionsStyle}>
                <button
                  onClick={selectAllScope}
                  style={tinyGhostBtnStyle}
                  disabled={scopeSelected.size === tables.length}
                >
                  select all
                </button>
                <button
                  onClick={clearAllScope}
                  style={tinyGhostBtnStyle}
                  disabled={scopeSelected.size === 0}
                >
                  clear
                </button>
                <div style={{ flex: 1 }} />
                {scopeMessage && (
                  <span style={{ color: "#1a6b3a", fontSize: "0.85rem" }}>
                    {scopeMessage}
                  </span>
                )}
                <button
                  onClick={saveScope}
                  disabled={scopeSaving}
                  style={
                    scopeSaving ? { ...buttonStyle, opacity: 0.6 } : buttonStyle
                  }
                >
                  {scopeSaving ? "Saving…" : "Save scope"}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {error && (
        <div style={errorBlockStyle}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {showConnect && (
        <ConnectDataSourceModal
          client={client}
          onClose={() => setShowConnect(false)}
          onCreated={handleDataSourceCreated}
        />
      )}

      {log.length > 0 && (
        <div style={logBlockStyle}>
          <h3>Provenance log ({log.length})</h3>
          <ol>
            {log.map((entry, i) => (
              <li key={i}>
                <code>{entry.operation_name}</code> {entry.success ? "✓" : "✗"}{" "}
                <span style={{ color: "#888" }}>
                  ({new Date(entry.timestamp).toLocaleTimeString()})
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}

// ── Sub-components ───────────────────────────────────────────────────

function HealthBlock({
  health,
  error,
}: {
  health: HealthResponse | null;
  error: string | null;
}) {
  if (error !== null) {
    return (
      <div style={errorBlockStyle}>
        <strong>Backend unreachable:</strong> {error}
        <div style={hintStyle}>
          Start: <code>uvicorn coframe.author.http:app --reload</code>
        </div>
      </div>
    );
  }
  if (health === null) {
    return <div style={{ color: "#888" }}>Pinging backend…</div>;
  }
  return (
    <div style={healthBlockStyle}>
      <strong>Backend OK.</strong> {health.ops_count} ops mounted ·{" "}
      {health.installations.length} installation(s):{" "}
      {health.installations.length > 0
        ? health.installations.join(", ")
        : "(none)"}
    </div>
  );
}

function formatError(e: unknown): string {
  if (e instanceof ApiError) return `${e.status} ${e.message}`;
  if (e instanceof Error) return e.message;
  return String(e);
}

// ── Inline styles (shared idiom; design system lands later) ───────────

const formRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "1rem",
  alignItems: "flex-end",
  marginBottom: "1rem",
};
const labelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  fontSize: "0.85rem",
  color: "#444",
};
const inputStyle: React.CSSProperties = {
  padding: "0.4rem 0.6rem",
  fontSize: "0.95rem",
  border: "1px solid #ccc",
  borderRadius: 4,
  minWidth: "20rem",
};
const buttonStyle: React.CSSProperties = {
  padding: "0.5rem 1rem",
  background: "#0366d6",
  color: "white",
  border: "none",
  borderRadius: 4,
  cursor: "pointer",
  fontSize: "0.95rem",
};
const healthBlockStyle: React.CSSProperties = {
  padding: "0.6rem 0.8rem",
  background: "#e6ffed",
  border: "1px solid #b7e1c1",
  borderRadius: 4,
  fontSize: "0.9rem",
  marginBottom: "1rem",
};
const errorBlockStyle: React.CSSProperties = {
  padding: "0.6rem 0.8rem",
  background: "#ffeef0",
  border: "1px solid #f3c0c0",
  borderRadius: 4,
  marginTop: "1rem",
  fontSize: "0.9rem",
};
const hintStyle: React.CSSProperties = {
  fontSize: "0.8rem",
  color: "#666",
  marginTop: "0.3rem",
};
const sessionBlockStyle: React.CSSProperties = {
  padding: "0.8rem 1rem",
  background: "#f7f7f7",
  border: "1px solid #ddd",
  borderRadius: 4,
};
const cellLabelStyle: React.CSSProperties = {
  color: "#666",
  paddingRight: "1rem",
  fontSize: "0.85rem",
};
const scopeBlockStyle: React.CSSProperties = {
  marginTop: "1.25rem",
  padding: "0.9rem 1rem",
  background: "#fafafa",
  border: "1px solid #ddd",
  borderRadius: 4,
};
const scopeHeaderRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: "0.75rem",
};
const scopeHelpStyle: React.CSSProperties = {
  margin: "0.25rem 0 0 0",
  color: "#666",
  fontSize: "0.85rem",
};
const scopeStatsStyle: React.CSSProperties = {
  fontSize: "0.9rem",
};
const scopeChipRowStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "0.4rem",
  marginBottom: "0.75rem",
};
const scopeChipOnStyle: React.CSSProperties = {
  padding: "0.3rem 0.65rem",
  fontSize: "0.85rem",
  background: "#eef4ff",
  color: "#0366d6",
  border: "1px solid #cdddf2",
  borderRadius: 14,
  cursor: "pointer",
  fontFamily: "monospace",
};
const scopeChipOffStyle: React.CSSProperties = {
  ...scopeChipOnStyle,
  background: "#fff",
  color: "#888",
  border: "1px solid #ddd",
};
const scopeActionsStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.4rem",
};
const tinyGhostBtnStyle: React.CSSProperties = {
  padding: "0.2rem 0.55rem",
  fontSize: "0.78rem",
  background: "#fff",
  color: "#444",
  border: "1px solid #cbd5e0",
  borderRadius: 3,
  cursor: "pointer",
};

const logBlockStyle: React.CSSProperties = {
  marginTop: "1.5rem",
  padding: "0.8rem 1rem",
  background: "#fafafa",
  border: "1px solid #ddd",
  borderRadius: 4,
};
